import { useRef, useState } from "react";
import { Loader2, Send } from "lucide-react";
import { http, errorMessage, fieldErrors } from "../../lib/api";
import { INDUSTRIES } from "../../content/site";
import { Field, TextInput, TextArea, SelectInput, CheckField, Honeypot, PrivacyAck, FormAlert, SuccessPanel } from "./Fields";

const EMPTY = {
  company: "", contact_name: "", email: "", phone: "", industry: "", position: "", headcount: "",
  location: "", start_date: "", message: "", privacy_ack: false, marketing_consent: false, website: "",
};

export const EmployerForm = () => {
  const [v, setV] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState({ state: "idle" });
  const started = useRef(Date.now());
  const set = (k, val) => { setV((p) => ({ ...p, [k]: val })); setErrors((e) => ({ ...e, [k]: undefined })); };

  const clientCheck = () => {
    const e = {};
    if (!v.company.trim()) e.company = "Bitte geben Sie „Unternehmen“ an.";
    if (!v.contact_name.trim()) e.contact_name = "Bitte geben Sie „Ansprechpartner“ an.";
    if (!/^\S+@\S+\.\S+$/.test(v.email.trim())) e.email = "Bitte geben Sie eine gültige E-Mail-Adresse an.";
    if (!v.position.trim()) e.position = "Bitte geben Sie „Gesuchter Beruf“ an.";
    if (!v.privacy_ack) e.privacy_ack = "Bitte bestätigen Sie, dass Sie die Datenschutzhinweise zur Kenntnis genommen haben.";
    return e;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    const e = clientCheck();
    if (Object.keys(e).length) {
      setErrors(e);
      setStatus({ state: "error", msg: "Bitte prüfen Sie die markierten Angaben." });
      document.getElementById(Object.keys(e)[0])?.focus();
      return;
    }
    setStatus({ state: "sending" });
    const fd = new FormData();
    Object.entries(v).forEach(([k, val]) => fd.append(k, typeof val === "boolean" ? String(val) : val));
    fd.append("started_at", String(started.current));
    try {
      const { data } = await http.post("/forms/inquiry", fd);
      setStatus({ state: "done", reference: data?.reference ?? "" });
    } catch (err) {
      setErrors(fieldErrors(err));
      setStatus({ state: "error", msg: errorMessage(err) });
    }
  };

  if (status.state === "done") {
    return (
      <SuccessPanel testid="inquiry-success" title="Vielen Dank – Ihre Anfrage ist bei uns eingegangen." reference={status.reference}
        onReset={() => { setV(EMPTY); started.current = Date.now(); setStatus({ state: "idle" }); }}>
        <p>Wir prüfen Ihre Angaben und melden uns per E-Mail bei Ihnen, um Ihren Personalbedarf im Detail zu besprechen.</p>
        <p>Bitte beachten Sie: Die Anfrage ist unverbindlich. Konditionen vereinbaren wir erst nach einem persönlichen Gespräch schriftlich.</p>
      </SuccessPanel>
    );
  }

  return (
    <form id="employer-form" onSubmit={submit} noValidate className="relative space-y-6" data-testid="employer-form">
      <Honeypot value={v.website} onChange={set} />
      <div className="grid gap-6 md:grid-cols-2">
        <Field id="company" label="Unternehmen" required error={errors.company}><TextInput id="company" value={v.company} onChange={set} error={errors.company} required autoComplete="organization" /></Field>
        <Field id="contact_name" label="Ansprechpartner" required error={errors.contact_name}><TextInput id="contact_name" value={v.contact_name} onChange={set} error={errors.contact_name} required autoComplete="name" /></Field>
        <Field id="email" label="E-Mail" required error={errors.email}><TextInput id="email" type="email" value={v.email} onChange={set} error={errors.email} required autoComplete="email" /></Field>
        <Field id="phone" label="Telefonnummer" error={errors.phone}><TextInput id="phone" type="tel" value={v.phone} onChange={set} error={errors.phone} autoComplete="tel" /></Field>
        <Field id="industry" label="Branche" error={errors.industry}><SelectInput id="industry" value={v.industry} onChange={set} error={errors.industry} options={INDUSTRIES.map((i) => i.title)} /></Field>
        <Field id="position" label="Gesuchter Beruf" required error={errors.position}><TextInput id="position" value={v.position} onChange={set} error={errors.position} required placeholder="z. B. Lkw-Fahrer (CE)" /></Field>
        <Field id="headcount" label="Anzahl gesuchter Mitarbeiter" error={errors.headcount}><TextInput id="headcount" type="number" min="1" max="9999" inputMode="numeric" value={v.headcount} onChange={set} error={errors.headcount} /></Field>
        <Field id="location" label="Einsatzort" error={errors.location}><TextInput id="location" value={v.location} onChange={set} error={errors.location} placeholder="z. B. Hamburg" /></Field>
        <Field id="start_date" label="Gewünschter Arbeitsbeginn" error={errors.start_date} className="md:col-span-2"><TextInput id="start_date" value={v.start_date} onChange={set} error={errors.start_date} placeholder="z. B. Frühjahr 2027 oder so bald wie möglich" /></Field>
        <Field id="message" label="Nachricht" error={errors.message} className="md:col-span-2"><TextArea id="message" value={v.message} onChange={set} error={errors.message} placeholder="Tätigkeit, Anforderungen, Arbeitszeit, Sprachniveau …" /></Field>
      </div>
      <div className="space-y-4 rounded-2xl bg-[color:var(--mist)] p-5 md:p-6">
        <PrivacyAck checked={v.privacy_ack} onChange={set} error={errors.privacy_ack} />
        <CheckField id="marketing_consent" checked={v.marketing_consent} onChange={set}>
          Freiwillig: Ich möchte gelegentlich per E-Mail Informationen von PACTORA zur internationalen Personalgewinnung erhalten. Diese Einwilligung kann ich jederzeit widerrufen.
        </CheckField>
      </div>
      {status.state === "error" && <FormAlert type="error">{status.msg}</FormAlert>}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-slate-500">Pflichtfelder sind mit * gekennzeichnet.</p>
        <button type="submit" disabled={status.state === "sending"} data-testid="inquiry-submit-button"
          className="inline-flex items-center justify-center gap-2 rounded-full bg-[color:var(--navy)] px-8 py-4 font-semibold text-white transition-[background-color,transform] hover:bg-[color:var(--navy-deep)] active:scale-[0.97] disabled:opacity-60">
          {status.state === "sending" ? <><Loader2 size={18} className="animate-spin" /> Wird gesendet …</> : <>Anfrage senden <Send size={17} /></>}
        </button>
      </div>
    </form>
  );
};
